import Modal from "@/components/modal"
import { t } from "i18next"
import { useState } from "react"
import Button from "@/components/button"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import { faWandMagicSparkles, faLightbulb } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface Props {
  onClose: () => void
  onContinue: () => void
}

export default function UnitDescriptionModal({ onClose, onContinue }: Props) {
  const dispatch = useAppDispatch()
  const draft = useAppSelector(unitStore.selectors.draft)
  const unitList = useAppSelector(unitStore.selectors.list)
  const [description, setDescription] = useState(draft.description || "")
  const [isSaving, setIsSaving] = useState(false)

  const handleContinueWithDescription = async () => {
    try {
      setIsSaving(true)
      dispatch(unitStore.actions.edit({ key: "description", value: description }))
      const exists = unitList.some((u) => u.id === draft.id)
      if (exists) {
        await dispatch(unitStore.thunks.updateEntity()).unwrap()
      }
      onContinue()
    } catch (err) {
      console.error("❌ Erreur lors de la sauvegarde", err)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isCloseButtonVisible
      onClose={onClose}
      className="max-w-2xl"
      disableOverflow
      footer={
        <Button
          className="primary"
          isWithBorder
          onClick={handleContinueWithDescription}
          isLoading={isSaving}
          icon={faWandMagicSparkles}>
          {t("Continuer")}
        </Button>
      }>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span>{t("Enrichir votre unité pour des suggestions plus pertinentes")}</span>
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            {t(
              "Pour que l'assistant puisse vous proposer des risques vraiment adaptés à votre unité de travail, une description détaillée de ses activités, son environnement et ses particularités est très utile. Sans elle, les suggestions seront plus génériques."
            )}
          </p>
          <p className="text-gray-500 text-sm italic">
            {t("Cette étape est optionnelle — vous pouvez continuer sans description si vous préférez.")}
          </p>
        </div>

        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
          <p className="text-sm text-gray-700 flex items-start gap-2">
            <FontAwesomeIcon icon={faLightbulb} className="text-gray-600 mt-0.5 flex-shrink-0" />
            <span>
              <strong>{t("Conseil :")} </strong>
              {t(
                "Décrivez les tâches principales, l'équipement utilisé, les risques connus et tout ce qui caractérise votre unité."
              )}
            </span>
          </p>
        </div>

        <div className="text-input text-input-fullwidth">
          <div className="text-input-title flex grey-t">{t("Description de l'unité")}</div>
          <textarea
            placeholder={t(
              "Ex: Atelier de menuiserie avec 8 personnes, utilisation de scies circulaires, raboteuses et presses hydrauliques. Travail sur bois massif et panneaux…"
            )}
            className="text-area"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
          />
        </div>
      </div>
    </Modal>
  )
}
